import React from "react";
import { Lock, ShieldCheck } from "lucide-react";

/**
 * PaymentTrustBadge — reusable trust block shown above every checkout button.
 *
 * Renders:
 *   • merchant disclosure ("Paying to DoozyGo® · a brand by Big Kreation Entertainment Pvt Ltd")
 *   • inline brand-faithful SVG logos for Visa · Mastercard · GPay · PhonePe · UPI · Netbanking
 *   • "Secured by Razorpay" lock badge
 *
 * Uses inline SVG (no CDN) so it never breaks on flaky networks or strict CSP.
 * Three layout variants:
 *   variant="compact" → single row, dense (default — for booking section)
 *   variant="full"    → 2-row stacked with extra padding (paywalls, modals)
 *   variant="dark"    → light text on dark bg (for hero / dark surfaces)
 */

const VisaLogo = () => (
  <svg viewBox="0 0 48 16" width="38" height="14" aria-label="Visa" role="img">
    <rect width="48" height="16" rx="2" fill="#1A1F71" />
    <text x="24" y="12" textAnchor="middle" fill="#FFFFFF" fontFamily="Arial Black, Arial, sans-serif" fontSize="10" fontWeight="900" letterSpacing="1">VISA</text>
  </svg>
);

const MastercardLogo = () => (
  <svg viewBox="0 0 40 24" width="32" height="20" aria-label="Mastercard" role="img">
    <rect width="40" height="24" rx="2" fill="#FFFFFF" stroke="#E5E7EB" />
    <circle cx="16" cy="12" r="6.5" fill="#EB001B" />
    <circle cx="24" cy="12" r="6.5" fill="#F79E1B" />
    <path d="M20 6.6a6.5 6.5 0 0 0 0 10.8 6.5 6.5 0 0 0 0-10.8z" fill="#FF5F00" />
  </svg>
);

const GPayLogo = () => (
  <svg viewBox="0 0 50 18" width="42" height="16" aria-label="Google Pay" role="img">
    <rect width="50" height="18" rx="3" fill="#FFFFFF" stroke="#E5E7EB" />
    <text x="6" y="13" fontFamily="Arial, sans-serif" fontSize="9" fontWeight="700" fill="#4285F4">G</text>
    <text x="12.5" y="13" fontFamily="Arial, sans-serif" fontSize="9" fontWeight="700" fill="#EA4335">o</text>
    <text x="18" y="13" fontFamily="Arial, sans-serif" fontSize="9" fontWeight="700" fill="#FBBC04">o</text>
    <text x="23.5" y="13" fontFamily="Arial, sans-serif" fontSize="9" fontWeight="700" fill="#4285F4">g</text>
    <text x="29" y="13" fontFamily="Arial, sans-serif" fontSize="9" fontWeight="700" fill="#34A853">l</text>
    <text x="32" y="13" fontFamily="Arial, sans-serif" fontSize="9" fontWeight="700" fill="#EA4335">e</text>
    <text x="38" y="13" fontFamily="Arial, sans-serif" fontSize="9" fontWeight="700" fill="#5F6368">Pay</text>
  </svg>
);

const PhonePeLogo = () => (
  <svg viewBox="0 0 56 18" width="46" height="16" aria-label="PhonePe" role="img">
    <rect width="56" height="18" rx="3" fill="#5F259F" />
    <circle cx="9" cy="9" r="5.5" fill="#FFFFFF" />
    <text x="9" y="12" textAnchor="middle" fontFamily="Arial Black, Arial, sans-serif" fontSize="8" fontWeight="900" fill="#5F259F">P</text>
    <text x="32.5" y="12.5" textAnchor="middle" fontFamily="Arial, sans-serif" fontSize="8.5" fontWeight="700" fill="#FFFFFF">PhonePe</text>
  </svg>
);

const UPILogo = () => (
  <svg viewBox="0 0 38 18" width="32" height="16" aria-label="UPI" role="img">
    <rect width="38" height="18" rx="3" fill="#FFFFFF" stroke="#E5E7EB" />
    <path d="M8 4 L11 4 L7.5 14 L4.5 14 Z" fill="#097939" />
    <path d="M12.5 4 L15.5 4 L12 14 L9 14 Z" fill="#ED752E" />
    <text x="26" y="12.5" textAnchor="middle" fontFamily="Arial, sans-serif" fontSize="7.5" fontWeight="800" fill="#0F766E">UPI</text>
  </svg>
);

const NetbankingLogo = ({ darkText = false }) => (
  <svg viewBox="0 0 64 18" width="56" height="16" aria-label="Netbanking" role="img">
    <rect width="64" height="18" rx="3" fill={darkText ? "#1F2937" : "#F1F5F9"} stroke="#E5E7EB" />
    <path d="M6 13 L6 7 L8 7 L8 13 Z M9 13 L9 6 L11 6 L11 13 Z M12 13 L12 8 L14 8 L14 13 Z M15 13 L15 5 L17 5 L17 13 Z" fill={darkText ? "#FFFFFF" : "#1E1B4B"} />
    <text x="40" y="12.5" textAnchor="middle" fontFamily="Arial, sans-serif" fontSize="7.5" fontWeight="700" fill={darkText ? "#FFFFFF" : "#1E1B4B"} letterSpacing="0.2">Netbanking</text>
  </svg>
);

const RazorpayMark = ({ darkText = false }) => (
  <svg viewBox="0 0 14 16" width="11" height="13" aria-label="Razorpay" role="img">
    <path d="M7.6 0 L4.4 6.6 L7 6.6 L4 16 L11 7.4 L8.4 7.4 L11.6 0 Z" fill={darkText ? "#FFFFFF" : "#3395FF"} />
  </svg>
);

const PaymentTrustBadge = ({ variant = "compact", className = "" }) => {
  const isDark = variant === "dark";
  const isFull = variant === "full";

  const merchantText = (
    <span className={`text-[10.5px] leading-tight ${isDark ? "text-white/80" : "text-[#475569]"}`}>
      You're paying <span className={`font-semibold ${isDark ? "text-white" : "text-[#1E1B4B]"}`}>DoozyGo®</span>
      <span className={isDark ? "text-white/60" : "text-[#94a3b8]"}> · a brand by </span>
      <span className={`font-semibold ${isDark ? "text-white" : "text-[#1E1B4B]"}`}>Big Kreation Entertainment Pvt Ltd</span>
    </span>
  );

  const logosRow = (
    <div className="flex items-center gap-1.5 flex-wrap" data-testid="payment-trust-logos">
      <VisaLogo />
      <MastercardLogo />
      <GPayLogo />
      <PhonePeLogo />
      <UPILogo />
      <NetbankingLogo darkText={isDark} />
    </div>
  );

  const securedRow = (
    <span className={`inline-flex items-center gap-1 text-[10px] font-medium ${isDark ? "text-white/80" : "text-[#475569]"}`}>
      <Lock size={10} className={isDark ? "text-white/70" : "text-emerald-600"} />
      Secured by
      <RazorpayMark darkText={isDark} />
      <span className={`font-bold ${isDark ? "text-white" : "text-[#3395FF]"}`}>Razorpay</span>
    </span>
  );

  if (isFull) {
    return (
      <div
        className={`rounded-xl border ${isDark ? "border-white/10 bg-white/5" : "border-gray-100 bg-[#FAFAFB]"} p-3 space-y-2 ${className}`}
        data-testid="payment-trust-badge"
      >
        <div className="flex items-start gap-2">
          <ShieldCheck size={14} className={isDark ? "text-emerald-400 flex-shrink-0 mt-0.5" : "text-emerald-600 flex-shrink-0 mt-0.5"} />
          {merchantText}
        </div>
        <div className="flex items-center justify-between gap-2 flex-wrap pl-5">
          {logosRow}
          {securedRow}
        </div>
      </div>
    );
  }

  // compact (default) — single tight block
  return (
    <div
      className={`rounded-lg ${isDark ? "bg-white/5 border border-white/10" : "bg-[#FAFAFB] border border-gray-100"} px-3 py-2 ${className}`}
      data-testid="payment-trust-badge"
    >
      <div className="flex items-center gap-1.5 mb-1.5">
        <ShieldCheck size={11} className={isDark ? "text-emerald-400" : "text-emerald-600"} />
        {merchantText}
      </div>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        {logosRow}
        {securedRow}
      </div>
    </div>
  );
};

export default PaymentTrustBadge;
